import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "isEditable", "isTooltipLoaded", "onClick", "showTooltip", "size", "tooltipPlacement", "tooltipTitle", "type"],
  _excluded2 = ["borderColor", "borderWidth", "borderStyle"];
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { useMapTokenPropsToCSS } from 'foundations-components/hooks/useMapTokenPropsToCSS';
import { isAvatarBorderGradient } from '../../types/SharedPropTypes';
import styled, { css } from 'styled-components';
import { getDimensions } from '../../lib/getDimensions';
import { useMemo } from 'react';
import { jsx as _jsx } from "react/jsx-runtime";
const AvatarWrapper = styled.div.withConfig({
  displayName: "AvatarContainer__AvatarWrapper"
})(["box-sizing:content-box;display:inline-flex;position:relative;transition-delay:150ms;transition:margin ease 300ms;flex-shrink:0;aspect-ratio:1 / 1;border-radius:", ";", " ", ""], ({
  $type
}) => $type === 'productId' ? '3px' : '50%', ({
  $dimensions
}) => $dimensions.height === null || $dimensions.width === null ? css(["inline-size:100%;"]) : css(["inline-size:", "px;block-size:", "px;"], $dimensions.width, $dimensions.height), ({
  $cssStyles
}) => $cssStyles);

// Renders an `AvatarBorderGradient` as a padding + `background` ring, since
// `border-color`/`border-image` can't express a gradient with correct
// circular rounding. Sized to 100% of the already-computed avatar footprint
// (`AvatarWrapper`'s `$dimensions`) rather than duplicating that number.
const AvatarBorderGradientRing = styled.div.withConfig({
  displayName: "AvatarContainer__AvatarBorderGradientRing"
})(["display:inline-flex;align-items:center;justify-content:center;box-sizing:border-box;inline-size:100%;block-size:100%;border-radius:inherit;padding:", ";background:", ";"], ({
  $borderWidth
}) => $borderWidth, ({
  $background
}) => $background);
const AvatarBorderGradientMask = styled.div.withConfig({
  displayName: "AvatarContainer__AvatarBorderGradientMask"
})(["display:inline-flex;align-items:center;justify-content:center;inline-size:100%;block-size:100%;border-radius:inherit;overflow:hidden;"]);
const getGradientBackground = ({
  type,
  colors
}) => {
  if (colors.length === 0) {
    return undefined;
  }
  if (type === 'linear-gradient') {
    return `linear-gradient(135deg, ${colors.join(', ')})`;
  }
  return `conic-gradient(${colors.join(', ')}, ${colors[0]})`;
};
export function AvatarContainer(_ref) {
  let {
      children,
      isEditable,
      isTooltipLoaded,
      onClick,
      showTooltip,
      size,
      tooltipPlacement,
      tooltipTitle,
      type
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
      borderColor,
      borderWidth,
      borderStyle
    } = rest,
    marginProps = _objectWithoutPropertiesLoose(rest, _excluded2);
  const gradient = isAvatarBorderGradient(borderColor) ? borderColor : undefined;
  const plainBorderColor = isAvatarBorderGradient(borderColor) ? undefined : borderColor;
  const cssStyles = useMapTokenPropsToCSS(Object.assign({}, marginProps, gradient ? {} : {
    borderColor: plainBorderColor,
    borderWidth,
    borderStyle
  }, {
    color: 'text-core-on-fill-default'
  }));

  // Only used to size the gradient ring's padding; never applied as an
  // actual CSS border (that would draw a stray currentColor line since
  // `borderColor`/`borderStyle` are intentionally omitted above).
  // `useMapTokenPropsToCSS` treats `borderWidth` as shorthand and expands it
  // into logical longhand keys rather than returning `borderWidth` itself;
  // all four are identical here since a single shorthand value was passed.
  const {
    borderBlockStartWidth: gradientBorderWidthStyle
  } = useMapTokenPropsToCSS(gradient ? {
    borderWidth
  } : {});
  const gradientBorderWidth = gradientBorderWidthStyle !== null && gradientBorderWidthStyle !== void 0 ? gradientBorderWidthStyle : 0;
  const dataTestIds = {
    'data-test-id': 'AvatarDisplay-switchComponent',
    'data-size': size,
    'data-type': type,
    'data-border-size': borderWidth,
    'data-tooltip-loaded': showTooltip && isTooltipLoaded
  };
  const dimensions = useMemo(() => getDimensions(size), [size]);
  const content = gradient ? /*#__PURE__*/_jsx(AvatarBorderGradientRing, {
    $background: getGradientBackground(gradient),
    $borderWidth: gradientBorderWidth,
    "data-test-id": "AvatarDisplay-borderGradientRing",
    children: /*#__PURE__*/_jsx(AvatarBorderGradientMask, {
      children: children
    })
  }) : children;
  if (!isEditable && showTooltip && isTooltipLoaded) {
    return /*#__PURE__*/_jsx(Tooltip, {
      "data-test-id": "AvatarDisplay-tooltip",
      placement: tooltipPlacement,
      title: tooltipTitle,
      children: /*#__PURE__*/_jsx(AvatarWrapper, Object.assign({
        $cssStyles: cssStyles,
        $dimensions: dimensions,
        $type: type,
        onClick: onClick
      }, dataTestIds, {
        children: content
      }))
    });
  }
  return /*#__PURE__*/_jsx(AvatarWrapper, Object.assign({
    $cssStyles: cssStyles,
    $dimensions: dimensions,
    $type: type,
    onClick: onClick
  }, dataTestIds, {
    children: content
  }));
}